import React, { FC, useEffect, useMemo, useState } from "react";
import { FileUploader } from "react-drag-drop-files";

import type { Action } from "components/Table";
import { Table } from "components/Table";
import { images } from "constant";
import { BinIcon, UploadIcon } from "icons";
import { ErrorText } from "styled";

import { Modal } from "../Modal";
import { Dialog, DialogActions, DialogContent, TextContent } from "../styled";

import { Cells } from "./config";
import type { UploadModalProps } from "./typed";
import {
  NoticeButton,
  TextDrag,
  TextError,
  TextLimit,
  TextUpload,
  UploadBox,
} from "./styled";

const EmptyError = {
  size: "",
  type: "",
  fileSize: "",
};

export const UploadModal: FC<UploadModalProps> = ({
  children,
  open,
  title,
  approveButton,
  cancelButton,
  fileTypes,
  maxSize,
  maxFiles,
  multiple,
  withUpload,
  isSuccess,
  isLoading,
  error,
  setOpen,
  onConfirm,
}) => {
  const [openNotice, setOpenNotice] = useState(false);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState(EmptyError);
  const actions = useMemo<Action[]>(
    () => [
      {
        label: "Delete Image",
        Icon: BinIcon,
        onClick: deletedFile =>
          setFiles(files.filter(file => file !== deletedFile)),
      },
    ],
    [files],
  );

  const handleChange = newFiles => {
    setErrors(EmptyError);

    if (multiple) {
      const restFiles = maxFiles - files.length;
      if (maxFiles && newFiles.length > restFiles) {
        setErrors({ ...EmptyError, size: `Max ${maxFiles} file(s) to upload` });
        setFiles([...files, ...Array.from(newFiles).slice(0, restFiles)]);
      } else {
        setFiles([...files, ...Array.from(newFiles)]);
      }
    } else {
      setFiles([newFiles]);
    }
  };
  const handleTypeError = err => setErrors({ ...errors, type: err });
  const handleSizeError = err => setErrors({ ...errors, fileSize: err });
  const handleClose = () => {
    setOpen(false);
    setFiles([]);
    setErrors(EmptyError);
  };
  const handleConfirm = () => {
    onConfirm(files);

    if (!withUpload) {
      setOpen(false);
      setOpenNotice(true);
    }
  };
  const closeNotice = () => {
    setOpenNotice(false);
    handleClose();
  };

  useEffect(() => {
    if (isSuccess && withUpload) {
      setOpen(false);
      setOpenNotice(true);
    }
  }, [withUpload, isSuccess, isLoading]);

  return (
    <>
      <Modal
        open={open}
        title={title}
        approveButton={approveButton}
        cancelButton={cancelButton}
        disabledApproveButton={!files.length || (withUpload && isLoading)}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
      >
        {children}
        <FileUploader
          multiple={multiple}
          handleChange={handleChange}
          name="file"
          types={fileTypes ?? undefined}
          maxSize={maxSize}
          onTypeError={handleTypeError}
          onSizeError={handleSizeError}
        >
          <UploadBox>
            <UploadIcon />
            <TextUpload>
              {`Upload a file `}
              <TextDrag component="span">or drag and drop</TextDrag>
            </TextUpload>
            <TextLimit>{`File Limit ${maxSize}MB`}</TextLimit>
          </UploadBox>
        </FileUploader>
        <TextError>{errors.size}</TextError>
        <TextError>{errors.type}</TextError>
        <TextError>{errors.fileSize}</TextError>
        {files.length ? (
          <Table
            rows={files}
            cells={Cells}
            actions={actions}
            withHeader={false}
            withPagination={false}
            firstColumnWidth={500}
          />
        ) : null}
        {error ? <ErrorText>{error}</ErrorText> : null}
      </Modal>

      <Dialog open={openNotice} maxWidth="sm" fullWidth onClose={closeNotice}>
        <DialogContent>
          <TextContent>Image has been uploaded successfully.</TextContent>
        </DialogContent>
        <DialogActions>
          <NoticeButton onClick={closeNotice}>Close</NoticeButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

UploadModal.defaultProps = {
  title: "Upload A File",
  fileTypes: images,
  maxSize: 10,
  maxFiles: 5,
  multiple: true,
};
